import request from 'sb-request'
import { getState, reducers } from 'redux/core'
import { getCookieValue, getFeatureToggle } from 'helpers'
import { array, constants, getGqlQuery, localStorage } from 'common/helpers'

import productBaseFragment from 'helpers/fragments/productBase.graphql'
import skincareGqlParams from './util/skincareGqlParams'


// TODO analyze logic in app and remove this reducer @akopylov 14.04.2020

const selectItem = reducers.view.pickPage.selectItem
const deselectItem = reducers.view.pickPage.deselectItem
const setFetching = reducers.view.pickPage.setFetching
const clearProducts = reducers.view.pickPage.clearProducts

/**
 * Clear selected products from store and localStorage
 */
const clearSelectedItems = () => {
  localStorage.removeItem(constants.localStorageNames.pickedProducts)
  localStorage.removeSessionItem(constants.localStorageNames.pickedProducts)
  reducers.view.pickPage.clearSelectedItems()
}

/**
 * Saves selected items to localStorage
 */
const saveSelectedItemsToStorage = () => {
  const { view: { pickPage: { selectedItems } } } = getState()

  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  const cleanedSelectedItems = selectedItems.map(({ gender, ...other }) => ({ ...other }))
  const data = {
    items: cleanedSelectedItems,
    metadata: { placement: 'pick page' },
  }

  if (isPremiumProductsForNotSubscribedEnabled) {
    localStorage.setSessionItem(constants.localStorageNames.pickedProducts, data)
  }
  else {
    localStorage.setItem(constants.localStorageNames.pickedProducts, data)
  }
}

/**
 * Get selected items from localStorage
 * We need to send items in register rest and event rest on Subscribe page
 *
 * @returns {Array} items
 */
const getSelectedItemsFromStorage = () => {
  moveSelectedItemsFromOldStorage()

  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  const { items } = isPremiumProductsForNotSubscribedEnabled
    ? localStorage.getSessionItem(constants.localStorageNames.pickedProducts) || {}
    : localStorage.getItem(constants.localStorageNames.pickedProducts) || {}

  return items || []
}

/**
 * Get selected items ids from localStorage
 * We need to send items in register rest and event rest on Subscribe page
 *
 * @returns {Array} items ids
 */
const getSelectedItemsIdsFromStorage = () => {
  const selectedItems = getSelectedItemsFromStorage()

  return selectedItems
    .map((item) => typeof item === 'number' ? item : item.id)
    .filter((id) => Boolean(id))
}

const moveSelectedItemsFromOldStorage = () => {
  // TODO remove it if you see it and date of adding is overgrown with moss - added on 15.07.2020 by sonatskiy
  const oldStorageName = 'pickYourScentFlowItems'
  const selectedItems = localStorage.getItem(oldStorageName)
  const selectedSessionItems = localStorage.getSessionItem(oldStorageName)
  const metadata = { placement: 'pick page' }

  if (selectedItems && selectedItems.length) {
    localStorage.setItem(constants.localStorageNames.pickedProducts, { items: selectedItems, metadata })
  }
  if (selectedSessionItems && selectedSessionItems.length) {
    localStorage.setSessionItem(constants.localStorageNames.pickedProducts, { items: selectedSessionItems, metadata })
  }

  localStorage.removeItem(oldStorageName)
  localStorage.removeSessionItem(oldStorageName)
}

const setSelectedItemsFromStorage = () => {
  const selectedItems = getSelectedItemsFromStorage()

  reducers.view.pickPage.setSelectedItems(selectedItems)
}

/**
 * Fetches "brands" by gender
 *
 * @returns {Promise} request
 */
const fetchBrandsByAlphabet = () => {
  const { models: { me: { gender } } } = getState()

  return request.post('graphql?name=perfumesByBrands', {
    body: {
      query: `{
        brandsLists: ${getGqlQuery('perfumesByBrand', { gender })} {
          brand
          logo
        }
      }`,
    },
    modifyResult: ({ brandsLists = [] }) => {
      const brandsByAlphabet = {}

      brandsLists
        .map(({ brand, logo }) => {
          const brandFirstLetter = /[A-Z]/.test(brand[0]) ? brand[0] : brand[1]

          return {
            logo,
            brand,
            brandFirstLetter,
          }
        })
        .sort((a, b) => {
          if (a.brandFirstLetter === b.brandFirstLetter) {
            return 0
          }

          return a.brandFirstLetter < b.brandFirstLetter ? -1 : 1
        })
        .forEach(({ logo, brand, brandFirstLetter }) => {

          if (brandsByAlphabet[brandFirstLetter]) {
            brandsByAlphabet[brandFirstLetter].push({ brand, logo })
          }
          else {
            brandsByAlphabet[brandFirstLetter] = [ { brand, logo } ]
          }
        })

      return brandsByAlphabet
    },
  })
    .then((brandsByAlphabet) => reducers.view.pickPage.setBrandsByAlphabet(brandsByAlphabet))
}

/**
 * Fetches products by brand filtered by gender
 *
 * @returns {Promise} request
 */
const fetchProductsByBrand = (brand) => {
  const { models: { me: { gender } } } = getState()

  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  let productsQueryParams = {
    gender,
    types: [],
    brands: [ brand ],
    groups: [ 'Perfume' ],
    productsFeed: 'ALL_PERFUMES',
    availableForQueue: true,
    hideUpchargeProducts: true,
  }

  if (isPremiumProductsForNotSubscribedEnabled) {
    productsQueryParams = {
      ...productsQueryParams,
      hideUpchargeProducts: false,
      minimumLepAvailable: 5,
    }
  }

  return request.post('graphql?name=fetchProductsByBrand', {
    body: {
      query: `{
        ${getGqlQuery('products', productsQueryParams)} {
          id
          image
          name
          category
          sex
        }
      }`,
    },
    modifyResult: ({ products }) => products.map((product) => ({ ...product, brand })),
  })
    .then((products) => reducers.view.pickPage.updateProductRecommendations({ id: brand, products }))
}

/**
 * Fetches similar products
 *
 * @returns {Promise} request
 */
const fetchSimilarProducts = (id) => {
  const { view: { pickPage: { productRecommendations } } } = getState()

  if (productRecommendations[id]) {
    return Promise.resolve()
  }

  return request.post('graphql?name=recommendationsByCategories', {
    body: {
      query: `{
        recommendationsByCategories(productId: ${id}){
          similar(limit: 10) {
            products {
              id
              image
              name
              brand
              category
              sex
            }
          }
        }
      }`,
    },
    modifyResult: ({ recommendationsByCategories }) => {
      const { similar } = recommendationsByCategories || {}

      return !similar ? [] : similar.products
    },
  })
    .then((products) => reducers.view.pickPage.updateProductRecommendations({ id, products }))
}

/**
 * Fetches "bestsellers" products (limited by 72) inside "Perfume" group filtered with gender
 *
 * @param {Object} params
 * @param {'male'|'female'} params.gender
 * @param {String} [ params.category ]
 * @param {Number} [ params.limit ]
 * @param {Number} [ params.offset ]
 * @param {String[]} [ params.groups ]
 * @param {String} [ params.productsFeed ]
 *
 * @returns {Promise} request
 */
const fetchPopularItemsByGender = (params) => {
  const { view: { pickPage: { isFetching } } } = getState()

  if (isFetching) {
    return
  }

  let {
    gender, category, groups = [ 'Perfume' ], limit = 72, offset = 0,
    productsFeed = 'PICK_ORDER_BRAND_PARTNERS',
  } = params

  reducers.view.pickPage.setFetching(true)

  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  let productsQueryParams = {
    limit,
    offset,
    gender,
    groups,
    category,
    productsFeed,
    availableForQueue: true,
    hideUpchargeProducts: true,
  }

  if (isPremiumProductsForNotSubscribedEnabled) {
    productsQueryParams = {
      ...productsQueryParams,
      hideUpchargeProducts: false,
      minimumLepAvailable: 5,
    }
  }

  request.post('graphql?name=fetchPopularItemsByGender', {
    body: {
      query: `{
        ${getGqlQuery('products', productsQueryParams)} {
          ...ProductBase
        }
      }
      ${productBaseFragment}`,
    },
  })
    .then(
      ({ products = [] }) => reducers.view.pickPage.updateProducts(products),
      () => {
        reducers.view.pickPage.setFetching(false)
        return Promise.reject()
      }
    )
}

const fetchMixPageProducts = (gender) => {
  reducers.view.pickPage.setFetching(true)

  const fragranceQueryParams = {
    gender,
    limit: 72,
    groups: 'Perfume',
    category: 'popularity',
    availableForQueue: true,
    productsFeed: 'PICK_ORDER_BRAND_PARTNERS',
  }

  const skincareQueryParams = {
    sort: 'PICK_PAGE',
    availableForQueue: true,
    includeProducts: gender === 'male'
      ? [ 1456, 1114, 1458, 1457, 1551, 1316, 1123, 1153, 1122, 1551, 1271 ]
      : [ 1133, 1536, 1013, 1490, 1257, 1244, 1344, 1247, 1397, 977, 978, 1005, 1138 ],
  }

  return request.post('graphql?name=fetchMixPageProducts', {
    body: {
      query: `{
        fragrance: ${getGqlQuery('products', fragranceQueryParams)} {
          ...ProductBase
        }
        skincare: ${getGqlQuery('products', skincareQueryParams)} {
          ...ProductBase
        }
      }
      ${productBaseFragment}`,
    },
  })
    .then(({ fragrance = [], skincare = [] }) => {
      reducers.view.pickPage.updateProducts([ ...fragrance, ...skincare ])
    })
    .finally(() => reducers.view.pickPage.setFetching(false))
}

const fetchPerfumesAndColognesByTags = (params) => {
  const { view: { pickPage: { isFetching } } } = getState()

  if (isFetching) {
    return
  }

  let {
    category,
    groups = 'Perfume',
    limit = 74,
    offset = 0,
    femaleTags = [],
    maleTags = [],
  } = params

  reducers.view.pickPage.setFetching(true)

  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  let productsQueryParams = {
    limit,
    offset,
    groups,
    category,
    productsFeed: 'PICK_ORDER_BRAND_PARTNERS',
    availableForQueue: true,
    hideUpchargeProducts: true,
  }

  if (isPremiumProductsForNotSubscribedEnabled) {
    productsQueryParams = {
      ...productsQueryParams,
      hideUpchargeProducts: false,
      minimumLepAvailable: 5,
    }
  }

  const perfumesParams = { ...productsQueryParams, gender: 'female', tagNames: femaleTags }
  const colognesParams = { ...productsQueryParams, gender: 'male', tagNames: maleTags }

  request.post('graphql?name=fetchPopularItemsByGender', {
    body: {
      query: `{
        perfumes: ${getGqlQuery('products', perfumesParams)} {
          ...ProductBase
        }
        colognes: ${getGqlQuery('products', colognesParams)} {
          ...ProductBase
        }
      }
      ${productBaseFragment}`,
    },
  })
    .then(
      ({ perfumes, colognes }) => {
        reducers.view.pickPage.setFetching(false)

        const modifiedPerfumes  = perfumes.map((perfume) => ({ ...perfume, gender: 'female' }))
        const modifiedColognes  = colognes.map((cologne) => ({ ...cologne, gender: 'male' }))
        const products          = [ ...modifiedPerfumes, ...modifiedColognes ]

        reducers.view.pickPage.setProducts(products)
      },
      () => {
        reducers.view.pickPage.setFetching(false)
        return Promise.reject()
      }
    )
}

const fetchSkincareProducts = () => {
  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  let productsQueryParams = {
    limit: 74,
    offset: 0,
    availableForQueue: true,
    hideUpchargeProducts: true,
  }

  if (isPremiumProductsForNotSubscribedEnabled) {
    productsQueryParams = {
      ...productsQueryParams,
      hideUpchargeProducts: false,
      minimumLepAvailable: 5,
    }
  }

  const queries = skincareGqlParams.map(({ customName, ...gqlParams }) => `
    ${customName}: ${getGqlQuery('products', { ...productsQueryParams, ...gqlParams })} {
      ...ProductBase
    }
  `)

  const query = `{
    ${queries.join('')}
  }
  ${productBaseFragment}`

  reducers.view.pickPage.setFetching(true)

  return request.post('graphql?name=fetchSkincareProducts', {
    body: { query },
  })
    .then((data) => {
      const products = Object.keys(data)
        .reduce((result, key) => ([
          ...result,
          ...data[key].map((item) => ({
            ...item,
            tag: key.replace(/_.*/, ''), // face_SOMETHING => face
          })),
        ]), [])

      reducers.view.pickPage.setProducts(array.shuffle(products))
    })
    .finally(() => reducers.view.pickPage.setFetching(false))
}


export default {
  selectItem,
  deselectItem,
  setFetching,
  saveSelectedItemsToStorage,
  getSelectedItemsIdsFromStorage,
  clearSelectedItems,
  clearProducts,
  setSelectedItemsFromStorage,
  fetchPerfumesAndColognesByTags,
  fetchProductsByBrand,
  fetchSimilarProducts,
  fetchBrandsByAlphabet,
  fetchPopularItemsByGender,
  fetchSkincareProducts,
  fetchMixPageProducts,
}
